import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import type { PracticeAgentChartDataPoint, PracticeAgentKpiCard, PracticeAgentReportData, PracticeAgentVisualizationType } from '../types'
import { classifyColumns, pickLabelColumn, deriveChartDataForDimension, getBarAxisMin } from '../utils'
import { fetchReportData, fetchWorkspaceInsight, fetchDrilldownData } from '../api'
import { productThemes } from '../../../lib/productBranding'

// Shared layout constants used by all PDF render functions
export const PDF_MARGIN = 10       // mm — page margin on all sides
export const PDF_IMAGE_TOP = 16    // mm — Y start of content area after page creation
export const PDF_TITLE_H = 8       // mm — vertical space consumed by a visual title line

// Returns true when `neededMm` fits within the remaining vertical space on the current page.
export function fitsOnPage(y: number, neededMm: number, pageH: number, margin: number): boolean {
  return y + neededMm <= pageH - margin
}

// Shared insight-box renderer (same teal style used by renderTableDirectToPdf).
// Adds a new page if the box does not fit at `y`. Returns the new Y after the box.
function renderInsightBox(
  pdf: jsPDF,
  insight: string,
  margin: number,
  y: number,
  imageMaxWidth: number,
  pageHeight: number,
  baseFontSize = 8,
): number {
  const cellPadH = 1.5
  const cellPadV = 2
  pdf.setFontSize(baseFontSize)
  pdf.setFont('helvetica', 'normal')
  const insightLines = pdf.splitTextToSize(insight, imageMaxWidth - cellPadH * 4) as string[]
  const labelH  = baseFontSize * 0.3528 + 3
  const bodyH   = insightLines.length * (baseFontSize * 0.3528 + 1)
  const boxH    = labelH + bodyH + cellPadV * 2 + 4

  y += 4 // gap above insight

  if (!fitsOnPage(y, boxH, pageHeight, margin)) {
    pdf.addPage('a4', pdf.internal.pageSize.getWidth() > pdf.internal.pageSize.getHeight() ? 'landscape' : 'portrait')
    y = PDF_IMAGE_TOP
  }

  pdf.setFillColor(240, 253, 250)
  pdf.setDrawColor(167, 243, 208)
  pdf.roundedRect(margin, y, imageMaxWidth, boxH, 2, 2, 'FD')

  pdf.setFontSize(baseFontSize * 0.85)
  pdf.setTextColor(4, 120, 87)
  pdf.setFont('helvetica', 'bold')
  pdf.text('AI INSIGHT', margin + cellPadH * 2, y + labelH)

  pdf.setFontSize(baseFontSize)
  pdf.setTextColor(55, 65, 81)
  pdf.setFont('helvetica', 'normal')
  const textStartY = y + labelH + cellPadV + baseFontSize * 0.3528
  insightLines.forEach((line, li) => {
    pdf.text(line, margin + cellPadH * 2, textStartY + li * (baseFontSize * 0.3528 + 1))
  })

  return y + boxH
}

// ── Bar chart ─────────────────────────────────────────────────────────────

// Compute evenly-spaced "nice" tick values for an axis [0, maxValue].
export function computeAxisTicks(maxValue: number, tickCount = 5): number[] {
  if (maxValue <= 0) return [0]
  const rawStep = maxValue / (tickCount - 1)
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const niceStep = Math.ceil(rawStep / mag) * mag
  const ticks: number[] = []
  for (let i = 0; i * niceStep <= maxValue * 1.01; i++) ticks.push(i * niceStep)
  return ticks
}

// "Nice" tick values spanning [minVal, maxVal] — handles non-zero min and negative values.
export function computeLineAxisTicks(minVal: number, maxVal: number, tickCount = 5): number[] {
  if (!Number.isFinite(minVal) || !Number.isFinite(maxVal)) return [0]
  const rawRange = maxVal - minVal
  if (rawRange === 0) {
    const offset = Math.abs(minVal) > 0 ? Math.abs(minVal) * 0.1 : 1
    return [minVal - offset, minVal, minVal + offset]
  }
  if (rawRange < 0) return [minVal, maxVal].sort((a, b) => a - b)
  const rawStep  = rawRange / (tickCount - 1)
  const mag      = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const niceStep = Math.ceil(rawStep / mag) * mag
  const start    = Math.floor(minVal / niceStep) * niceStep
  const ticks: number[] = []
  for (let t = start; ticks.length <= tickCount + 2; t = parseFloat((t + niceStep).toFixed(10))) {
    if (t >= minVal - niceStep * 0.01) ticks.push(t)
    if (t > maxVal + niceStep * 0.01) break
  }
  return ticks
}

// Format a tick value: integer when whole, otherwise 1 decimal place.
export function formatTickValue(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

// Port of the UI's generateGroupedBarColors() using the PracIntel base color.
export function generateGroupedBarRgbColors(count: number): [number, number, number][] {
  if (count <= 0) return []
  const hex = productThemes.pracIntel.primary
  const rn = parseInt(hex.slice(1, 3), 16) / 255
  const gn = parseInt(hex.slice(3, 5), 16) / 255
  const bn = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn), d = max - min
  let h = 0
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6
    else if (max === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    h *= 60
  }
  if (h < 0) h += 360
  const l = (max + min) / 2
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  const baseH = h, baseS = s * 100, baseL = l * 100
  const spread = 60
  const clp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
  return Array.from({ length: count }, (_, i) => {
    const t = count === 1 ? 0 : i / (count - 1)
    const hue = (baseH + (t - 0.5) * spread + 360) % 360
    const sat = clp(baseS + (t - 0.5) * 10, 40, 90) / 100
    const lig = clp(baseL + (t - 0.5) * 8, 35, 75) / 100
    const c = (1 - Math.abs(2 * lig - 1)) * sat
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
    const m = lig - c / 2
    let r = 0, g = 0, b = 0
    if      (hue < 60)  [r, g, b] = [c, x, 0]
    else if (hue < 120) [r, g, b] = [x, c, 0]
    else if (hue < 180) [r, g, b] = [0, c, x]
    else if (hue < 240) [r, g, b] = [0, x, c]
    else if (hue < 300) [r, g, b] = [x, 0, c]
    else                [r, g, b] = [c, 0, x]
    return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)] as [number, number, number]
  })
}

export function hexToHsl(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }

  if (h < 0) h += 360;

  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

  return {
    h,
    s: s * 100,
    l: l * 100
  };
}

export function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

/**
 * Draws a label on the PDF with optional wrapping, rotation, max lines, and margins
 */
export function wrapLabelLines(text: string, maxCharsPerLine: number, maxLines: number): string[] {
  const words = String(text).split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    if ((currentLine + word).length > maxCharsPerLine) {
      lines.push(currentLine.trim())
      currentLine = word + ' '
    } else {
      currentLine += word + ' '
    }
  }
  if (currentLine) lines.push(currentLine.trim())

  const finalLines = lines.slice(0, maxLines)
  if (lines.length > maxLines) {
    const lastIndex = maxLines - 1
    finalLines[lastIndex] = finalLines[lastIndex]?.replace(/\s+$/, '') + '...'
  }
  return finalLines
}

export function drawWrappedLabel(
  doc: jsPDF,
  text: string,
  xPos: number,
  yPos: number,
  options?: {
    maxCharsPerLine?: number
    maxLines?: number
    fontSize?: number
    lineHeight?: number
    shortLineSpacingRatio?: number
    longLineSpacingRatio?: number
    angle?: number
    color?: [number, number, number]
    margin?: { top?: number; right?: number; bottom?: number; left?: number }
    align?: 'left' | 'center' | 'right'
  }
): { bottomY: number } {
  const {
    maxCharsPerLine = 20,
    maxLines = 2,
    fontSize = 6,
    lineHeight = 6,
    shortLineSpacingRatio = 0.1,
    longLineSpacingRatio = 3.1,
    angle = 35,
    color = [142, 142, 142],
    margin: labelMargin = {},
    align = 'right',
  } = options || {}

  const { top = 0, right = 0, bottom = 0, left = 0 } = labelMargin

  const adjustedX = xPos + (left - right)
  const adjustedY = yPos + (top - bottom)
  const pxToMm = 0.264583
  const fontSizeMm = fontSize * 0.3528
  // Honor caller-provided lineHeight for wrap flow (UI-style px converted to mm).
  const lineHeightMm = Math.max(lineHeight * pxToMm, 0.1)
  const theta = (angle * Math.PI) / 180
  const ascentMm = fontSizeMm * 0.75
  const descentMm = fontSizeMm * 0.25
  const topGapMm = 2

  doc.setFontSize(fontSize)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...color)

  const finalLines = wrapLabelLines(text, maxCharsPerLine, maxLines)
  const lineWidthsMm = finalLines.map((line) => doc.getTextWidth(line))
  const widestLineMm = Math.max(...lineWidthsMm, 0.001)
  const baseBaselineY = adjustedY + topGapMm + (ascentMm * Math.cos(theta))

  // Apply adaptive spacing: shorter wrapped lines are tighter, longer lines looser.
  // Spacing remains proportional to lineHeight for visual consistency with UI ticks.
  let lastBaselineY = baseBaselineY
  let cumulativeAdvanceMm = 0
  const shortRatio = Math.max(shortLineSpacingRatio, 0.1)
  const longRatio = Math.max(longLineSpacingRatio, 0.1)
  finalLines.forEach((line, index) => {
    const lineWidthMm = lineWidthsMm[index] ?? 0
    const widthRatio = Math.min(Math.max(lineWidthMm / widestLineMm, 0), 1)
    const spacingFactor = shortRatio + ((longRatio - shortRatio) * widthRatio)
    const adaptiveStepMm = lineHeightMm * spacingFactor

    const baselineX = adjustedX + (Math.sin(theta) * cumulativeAdvanceMm)
    const baselineY = baseBaselineY + (lineWidthMm * Math.sin(theta)) + (Math.cos(theta) * cumulativeAdvanceMm)
    lastBaselineY = baselineY
    doc.text(line, baselineX, baselineY, {
      angle,
      align,
    })
    cumulativeAdvanceMm += adaptiveStepMm
  })

  const bottomY = lastBaselineY + descentMm * Math.cos(theta)
  return { bottomY }
}

// Draw the x-axis scale strip (axis line + tick marks + labels) at the given Y.
// Returns the new Y after the strip (axis line height is X_AXIS_STRIP_H mm).
const X_AXIS_STRIP_H = 8 // mm
function drawXAxisScale(
  pdf: jsPDF,
  ticks: number[],
  minValue: number,
  maxValue: number,
  barAreaX: number,     // x start of the bar area (after label column)
  barAreaW: number,     // width of bar area
  y: number,
): void {
  const range = Math.max(maxValue - minValue, 0.001)
  // Axis baseline
  pdf.setDrawColor(209, 213, 219)
  pdf.setLineWidth(0.3)
  pdf.line(barAreaX, y, barAreaX + barAreaW, y)
  pdf.setLineWidth(0.2)

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(6)
  pdf.setTextColor(107, 114, 128)

  for (const tick of ticks) {
    const tx = barAreaX + ((tick - minValue) / range) * barAreaW
    // Tick mark
    pdf.setDrawColor(209, 213, 219)
    pdf.line(tx, y, tx, y + 2)
    // Label
    pdf.text(formatTickValue(tick), tx, y + 5, { align: 'center' })
  }
}

function computeBarAxisTicks(minValue: number, maxValue: number, tickCount = 5): number[] {
  if (minValue >= 0) return computeAxisTicks(maxValue)
  if (maxValue <= minValue) return [minValue]

  const step = (maxValue - minValue) / Math.max(tickCount - 1, 1)
  const ticks = Array.from({ length: tickCount }, (_, i) => minValue + step * i)
  if (minValue < 0 && maxValue > 0 && !ticks.some((tick) => Math.abs(tick) < step * 0.2)) {
    ticks.push(0)
  }
  return Array.from(new Set(ticks.map((tick) => parseFloat(tick.toFixed(10))))).sort((a, b) => a - b)
}

export function renderBarChartDirectToPdf(
  pdf: jsPDF,
  chartData: PracticeAgentChartDataPoint[],
  title: string,
  insight: string | undefined,
  margin: number,
  startY: number,
  imageMaxWidth: number,
  pageHeight: number,
): number {
  const LABEL_W    = 50          // mm reserved for category labels on the left
  const BAR_AREA_W = imageMaxWidth - LABEL_W  // width of the bar + axis area
  const BAR_H      = 7           // mm per bar row
  const BAR_PAD    = 1.5         // mm gap between bar rows
  const ROW_H      = BAR_H + BAR_PAD

  let y = startY

  // Title
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(11)
  pdf.setTextColor(17, 24, 39)
  pdf.text(title, margin, y + 6)
  y += PDF_TITLE_H

  if (chartData.length === 0) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8)
    pdf.setTextColor(107, 114, 128)
    pdf.text('No data available.', margin, y + 6)
    return y + 10
  }

  const barAreaX = margin + LABEL_W
  const values = chartData.map(d => d.value)
  const minValue = getBarAxisMin(values)
  const maxValue = Math.max(...values, minValue < 0 ? 0 : 1)
  const valueRange = Math.max(maxValue - minValue, 0.001)
  const zeroX = barAreaX + ((0 - minValue) / valueRange) * BAR_AREA_W
  const ticks = computeBarAxisTicks(minValue, maxValue)

  // Helper: draw x-axis scale + faint vertical grid lines for one page section of bars.
  // Pass the Y of the first bar row on this page section.
  const drawScaleForSection = (sectionStartY: number, sectionEndY: number) => {
    // Faint vertical grid lines from sectionStartY down to sectionEndY
    pdf.setDrawColor(220, 220, 220)
    pdf.setLineWidth(0.15)
    for (const tick of ticks) {
      if (tick === 0) continue
      const tx = barAreaX + ((tick - minValue) / valueRange) * BAR_AREA_W
      pdf.line(tx, sectionStartY, tx, sectionEndY)
    }
    pdf.setLineWidth(0.2)
    // Axis strip below bars
    drawXAxisScale(pdf, ticks, minValue, maxValue, barAreaX, BAR_AREA_W, sectionEndY)
  }

  // Collect bars per page, then draw the axis strip after each section.
  // Two-pass: first pass records where each page section starts/ends.
  // Instead, single-pass with deferred axis draw:
  let sectionTopY = y
  let prevY = y
  for (let idx = 0; idx < chartData.length; idx++) {
    const point = chartData[idx]
    if (!point) continue
    const isLast = idx === chartData.length - 1

    if (!fitsOnPage(y, ROW_H + (isLast ? X_AXIS_STRIP_H : 0), pageHeight, margin)) {
      // Draw axis strip for the section we just finished, then start new page
      drawScaleForSection(sectionTopY, prevY)
      pdf.addPage('a4', 'portrait')
      y = PDF_IMAGE_TOP
      sectionTopY = y
    }

    const valueX = barAreaX + ((point.value - minValue) / valueRange) * BAR_AREA_W
    const barX = Math.min(zeroX, valueX)
    const barW = Math.abs(valueX - zeroX)

    // Bar (teal — matches UI #15B79E)
    pdf.setFillColor(21, 183, 158)
    if (barW > 0) pdf.rect(barX, y + 1, barW, BAR_H - 2, 'F')

    // Label (truncated to fit LABEL_W - 4mm)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(7.5)
    pdf.setTextColor(55, 65, 81)
    const labelLine = (pdf.splitTextToSize(point.label, LABEL_W - 4) as string[])[0] ?? ''
    pdf.text(labelLine, margin, y + BAR_H - 2)

    prevY = y + ROW_H
    y += ROW_H
  }

  // Draw axis strip for the final page section
  drawScaleForSection(sectionTopY, y)
  y += X_AXIS_STRIP_H

  if (insight) {
    y = renderInsightBox(pdf, insight, margin, y, imageMaxWidth, pageHeight)
  }

  return y
}

// ── Line chart ────────────────────────────────────────────────────────────
// 20 data points per landscape page; Y scale is global across all pages.
const LINE_CHART_POINTS_PER_PAGE = 20
const LINE_CHART_H   = 130  // mm — plot area height (fits A4 landscape 210mm)
const LINE_X_AXIS_H  = 35   // mm — space below plot area for angled x-axis labels (sized for fontSize 11pt at 35°, 20 char max)
const LINE_PLOT_PAD_L = 22  // mm — left gutter for y-axis labels (sized for fontSize 11pt)
const LINE_PLOT_PAD_R = 6   // mm — right gutter

export function renderLineChartDirectToPdf(
  pdf: jsPDF,
  chartData: PracticeAgentChartDataPoint[],
  title: string,
  insight: string | undefined,
  margin: number,
  startY: number,
  _imageMaxWidth: number,
  _pageHeight: number,
  axisMinValue?: number | null,
  axisMaxValue?: number | null,
): number {
  let y = startY

  // Title on the (already-created) first page
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(11)
  pdf.setTextColor(17, 24, 39)
  pdf.text(title, margin, y + 6)
  y += PDF_TITLE_H

  if (chartData.length === 0) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8)
    pdf.setTextColor(107, 114, 128)
    pdf.text('No data available.', margin, y + 6)
    return y + 10
  }

  // Global Y scale — mirrors UI: use server-provided domain if available, else dataMin/dataMax
  const rawMin    = Math.min(...chartData.map(d => d.value))
  const rawMax    = Math.max(...chartData.map(d => d.value))
  const domainMin = axisMinValue ?? rawMin
  const domainMax = axisMaxValue ?? rawMax
  const niceLineTicks = computeLineAxisTicks(domainMin, domainMax)
  const minVal = niceLineTicks[0] ?? domainMin
  const maxVal = niceLineTicks[niceLineTicks.length - 1] ?? domainMax
  const range  = Math.max(maxVal - minVal, 0.001)

  // Split into chunks of LINE_CHART_POINTS_PER_PAGE
  const chunks: PracticeAgentChartDataPoint[][] = []
  for (let s = 0; s < chartData.length; s += LINE_CHART_POINTS_PER_PAGE) {
    chunks.push(chartData.slice(s, s + LINE_CHART_POINTS_PER_PAGE))
  }

  for (let ci = 0; ci < chunks.length; ci++) {
    const chunk = chunks[ci]
    if (!chunk || chunk.length === 0) continue
    const isFirst = ci === 0

    if (!isFirst) {
      // Each continuation chunk gets its own landscape page
      pdf.addPage('a4', 'landscape')
      y = PDF_IMAGE_TOP
    }

    // Re-read page width in case jsPDF flipped dimensions
    const pw       = pdf.internal.pageSize.getWidth()
    const imgMaxW  = pw - margin * 2
    const chartLeft = margin + LINE_PLOT_PAD_L
    const plotW     = imgMaxW - LINE_PLOT_PAD_L - LINE_PLOT_PAD_R
    const n         = chunk.length
    const chartTop  = y

    const toX = (i: number) => chartLeft + (i / Math.max(n - 1, 1)) * plotW
    const toY = (v: number) => chartTop + LINE_CHART_H - ((v - minVal) / range) * LINE_CHART_H

    // Y-axis grid lines, tick marks, and labels
    for (const val of niceLineTicks) {
      const ty = toY(val)
      // Dashed grid line matching CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"
      pdf.setDrawColor(240, 240, 240)
      pdf.setLineWidth(0.2)
      pdf.setLineDashPattern([1.5, 1.5], 0)
      pdf.line(chartLeft, ty, chartLeft + plotW, ty)
      pdf.setLineDashPattern([], 0)
      // Tick mark: 2mm horizontal line extending left from y-axis, stroke="#666"
      pdf.setDrawColor(102, 102, 102)
      pdf.setLineWidth(0.3)
      pdf.line(chartLeft - 2, ty, chartLeft, ty)
      // Tick label: fontSize 11, color #666 — matches recharts YAxis tick={{ fontSize: 11 }}
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(6)
      pdf.setTextColor(102, 102, 102)
      pdf.text(formatTickValue(val), chartLeft - 3, ty + 1, { align: 'right' })
    }
    // Y-axis line: vertical line on left edge, stroke="#666" — matches recharts default
    pdf.setDrawColor(102, 102, 102)
    pdf.setLineWidth(0.3)
    pdf.line(chartLeft, chartTop, chartLeft, chartTop + LINE_CHART_H)

    // X-axis line: stroke="#666" — matches recharts default
    pdf.setDrawColor(102, 102, 102)
    pdf.setLineWidth(0.3)
    pdf.line(chartLeft, chartTop + LINE_CHART_H, chartLeft + plotW, chartTop + LINE_CHART_H)
    // X-axis tick marks at each data point: 2mm downward, stroke="#666" — matches recharts default
    for (let i = 0; i < n; i++) {
      pdf.line(toX(i), chartTop + LINE_CHART_H, toX(i), chartTop + LINE_CHART_H + 2)
    }

    // Line segments (teal — matches UI #15B79E)
    pdf.setDrawColor(21, 183, 158)
    pdf.setLineWidth(0.5)
    for (let i = 1; i < n; i++) {
      const prevPoint = chunk[i - 1]
      const currPoint = chunk[i]
      if (!prevPoint || !currPoint) continue
      pdf.line(toX(i - 1), toY(prevPoint.value), toX(i), toY(currPoint.value))
    }

    // Dots
    pdf.setFillColor(21, 183, 158)
    for (let i = 0; i < n; i++) {
      const point = chunk[i]
      if (!point) continue
      pdf.circle(toX(i), toY(point.value), 1.2, 'F')
    }
    pdf.setLineWidth(0.2)

    // Vertical grid lines at each x tick: dashed, matching CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"
    pdf.setDrawColor(240, 240, 240);
    pdf.setLineWidth(0.2);
    pdf.setLineDashPattern([1.5, 1.5], 0);
    for (let i = 0; i < n; i++) {
      const x = toX(i);
      pdf.line(x, chartTop, x, chartTop + LINE_CHART_H);
    }
    pdf.setLineDashPattern([], 0);

    let maxLabelBottomY = chartTop + LINE_CHART_H
    for (let i = 0; i < n; i++) {
      const point = chunk[i]
      if (!point) continue
      const { bottomY } = drawWrappedLabel(pdf, point.label, toX(i), chartTop + LINE_CHART_H, {
        maxCharsPerLine: 20,
        maxLines: 2,
        fontSize: 6,
        lineHeight: 3,
        angle: 35,
        color: [142, 142, 142],
        margin: { top: 0, right: -3, bottom: 0, left: 0 },
        align: 'right',
      });
      if (bottomY > maxLabelBottomY) maxLabelBottomY = bottomY
    }

    const dynamicXAxisLabelStripH = Math.max(LINE_X_AXIS_H, (maxLabelBottomY - (chartTop + LINE_CHART_H)) + 2)
    y = chartTop + LINE_CHART_H + dynamicXAxisLabelStripH
  }

  if (insight) {
    const pw      = pdf.internal.pageSize.getWidth()
    const imgMaxW = pw - margin * 2
    const ph      = pdf.internal.pageSize.getHeight()
    y = renderInsightBox(pdf, insight, margin, y, imgMaxW, ph)
  }

  return y
}

// ── Grouped bar chart ─────────────────────────────────────────────────────

export function renderGroupedBarChartDirectToPdf(
  pdf: jsPDF,
  chartData: PracticeAgentChartDataPoint[],
  title: string,
  insight: string | undefined,
  margin: number,
  startY: number,
  imageMaxWidth: number,
  pageHeight: number,
): number {
  const hasGroups = chartData.some(d => d.series && d.series.length > 0)
  if (!hasGroups) {
    throw new Error(`Grouped bar export requires grouped series data for "${title}".`)
  }

  const LABEL_W   = 50
  const VALUE_AREA = imageMaxWidth - LABEL_W
  const SUB_BAR_H  = 3.0  // mm per individual series sub-bar
  const GROUP_GAP  = 3    // mm gap between groups
  const seriesNames = chartData[0]?.series?.map((s: { name: string; value: number }) => s.name) ?? []
  const seriesCount = seriesNames.length
  const GROUP_ROW_H = seriesCount * SUB_BAR_H + GROUP_GAP

  let y = startY

  // Title
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(11)
  pdf.setTextColor(17, 24, 39)
  pdf.text(title, margin, y + 6)
  y += PDF_TITLE_H

  if (chartData.length === 0) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(8)
    pdf.setTextColor(107, 114, 128)
    pdf.text('No data available.', margin, y + 6)
    return y + 10
  }

  const palette = generateGroupedBarRgbColors(seriesCount)
  const barAreaX = margin + LABEL_W
  const BAR_AREA_W = VALUE_AREA

  // Legend strip
  const drawLegend = (ly: number): number => {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(6.5)
    let lx = margin
    const SWATCH = 3
    const SWATCH_GAP = 2
    const ITEM_GAP = 6
    seriesNames.forEach((name: string, si: number) => {
      const color = palette[si % Math.max(palette.length, 1)] ?? [62, 196, 187]
      const [r, g, b] = color
      pdf.setFillColor(r, g, b)
      pdf.rect(lx, ly, SWATCH, SWATCH, 'F')
      pdf.setTextColor(55, 65, 81)
      pdf.text(name.slice(0, 18), lx + SWATCH + SWATCH_GAP, ly + SWATCH - 0.5)
      lx += SWATCH + SWATCH_GAP + pdf.getStringUnitWidth(name.slice(0, 18)) * 6.5 / pdf.internal.scaleFactor + ITEM_GAP
    })
    return ly + SWATCH + 3
  }

  y = drawLegend(y)

  const values = chartData.flatMap(d => d.series?.map((s: { name: string; value: number }) => s.value) ?? [d.value])
  const minVal = getBarAxisMin(values)
  const maxVal = Math.max(...values, minVal < 0 ? 0 : 1)
  const valueRange = Math.max(maxVal - minVal, 0.001)
  const zeroX = barAreaX + ((0 - minVal) / valueRange) * BAR_AREA_W
  const ticks = computeBarAxisTicks(minVal, maxVal)

  const drawScaleForSection = (sectionTopY: number, sectionEndY: number) => {
    pdf.setDrawColor(220, 220, 220)
    pdf.setLineWidth(0.15)
    for (const tick of ticks) {
      if (tick === 0) continue
      const tx = barAreaX + ((tick - minVal) / valueRange) * BAR_AREA_W
      pdf.line(tx, sectionTopY, tx, sectionEndY)
    }
    pdf.setLineWidth(0.2)
    drawXAxisScale(pdf, ticks, minVal, maxVal, barAreaX, BAR_AREA_W, sectionEndY)
  }

  let sectionTopY = y
  let prevY = y
  for (let idx = 0; idx < chartData.length; idx++) {
    const point = chartData[idx]
    if (!point) continue
    const isLast = idx === chartData.length - 1

    if (!fitsOnPage(y, GROUP_ROW_H + (isLast ? X_AXIS_STRIP_H : 0), pageHeight, margin)) {
      drawScaleForSection(sectionTopY, prevY)
      pdf.addPage('a4', 'portrait')
      y = PDF_IMAGE_TOP
      y = drawLegend(y)
      sectionTopY = y
    }

    // Group label
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(7.5)
    pdf.setTextColor(55, 65, 81)
    const labelLine = (pdf.splitTextToSize(point.label, LABEL_W - 4) as string[])[0] ?? ''
    pdf.text(labelLine, margin, y + (seriesCount * SUB_BAR_H) / 2 + 1)

    const series = point.series ?? [{ name: '', value: point.value }]
    series.forEach((s: { name: string; value: number }, si: number) => {
      const color = palette[si % Math.max(palette.length, 1)] ?? [62, 196, 187]
      const [r, g, b] = color
      const valueX = barAreaX + ((s.value - minVal) / valueRange) * BAR_AREA_W
      const barX = Math.min(zeroX, valueX)
      const barW = Math.abs(valueX - zeroX)
      const barY = y + si * SUB_BAR_H

      pdf.setFillColor(r, g, b)
      if (barW > 0) pdf.rect(barX, barY, barW, SUB_BAR_H - 0.5, 'F')
      if (barW > 10) {
        pdf.setFontSize(5.5)
        pdf.setTextColor(255, 255, 255)
        pdf.text(String(s.value), Math.max(zeroX, valueX) - 1, barY + SUB_BAR_H - 1.2, { align: 'right' })
      }
    })

    prevY = y + GROUP_ROW_H
    y += GROUP_ROW_H
  }

  drawScaleForSection(sectionTopY, y)
  y += X_AXIS_STRIP_H

  if (insight) {
    y = renderInsightBox(pdf, insight, margin, y, imageMaxWidth, pageHeight)
  }

  return y
}

// ── KPI cards ─────────────────────────────────────────────────────────────
export function renderKpiDirectToPdf(
  pdf: jsPDF,
  kpiCards: PracticeAgentKpiCard[],
  _chartData: PracticeAgentChartDataPoint[],
  title: string,
  insight: string | undefined,
  margin: number,
  startY: number,
  imageMaxWidth: number,
  pageHeight: number,
): number {
  if (kpiCards.length === 0) {
    throw new Error(`KPI export requires KPI card data for "${title}".`)
  }

  const COLS     = 2
  const CARD_H   = 24  // mm per card row
  const CARD_GAP = 4   // mm between cards
  const cardW    = (imageMaxWidth - CARD_GAP * (COLS - 1)) / COLS

  let y = startY

  // Title
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(11)
  pdf.setTextColor(17, 24, 39)
  pdf.text(title, margin, y + 6)
  y += PDF_TITLE_H

  // Chunk into rows of COLS
  for (let ri = 0; ri < kpiCards.length; ri += COLS) {
    const rowCards = kpiCards.slice(ri, ri + COLS)

    if (!fitsOnPage(y, CARD_H + CARD_GAP, pageHeight, margin)) {
      pdf.addPage('a4', 'portrait')
      y = PDF_IMAGE_TOP
    }

    rowCards.forEach((kpi, ci) => {
      const cardX = margin + ci * (cardW + CARD_GAP)

      // Background + border
      pdf.setFillColor(249, 250, 251)
      pdf.setDrawColor(229, 231, 235)
      pdf.roundedRect(cardX, y, cardW, CARD_H, 2, 2, 'FD')

      // Label
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(7)
      pdf.setTextColor(107, 114, 128)
      pdf.text((pdf.splitTextToSize(kpi.label, cardW - 6) as string[])[0] ?? '', cardX + 3, y + 7)

      // Value
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(14)
      pdf.setTextColor(17, 24, 39)
      const displayValue = String(kpi.value) + (kpi.unit ? ` ${kpi.unit}` : '')
      pdf.text((pdf.splitTextToSize(displayValue, cardW - 6) as string[])[0] ?? '', cardX + 3, y + 17)

      // Measure label
      if (kpi.measureLabel) {
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(6)
        pdf.setTextColor(156, 163, 175)
        pdf.text((pdf.splitTextToSize(kpi.measureLabel, cardW - 6) as string[])[0] ?? '', cardX + 3, y + 22)
      }
    })

    y += CARD_H + CARD_GAP
  }

  if (insight) {
    y = renderInsightBox(pdf, insight, margin, y, imageMaxWidth, pageHeight)
  }

  return y
}

export function renderTableDirectToPdf(
  pdf: jsPDF,
  data: PracticeAgentReportData,
  title: string,
  insight: string | undefined,
  margin: number,
  imageTop: number,
  imageMaxWidth: number,
  pageHeight: number,
): void {
  // ── Base dimensions (unscaled) ────────────────────────────────────────────
  const BASE_FONT_SIZE_HEADER = 8.5
  const BASE_FONT_SIZE_DATA   = 9
  const BASE_LINE_H    = 5    // mm per wrapped text line
  const BASE_CELL_PAD_V = 2   // vertical padding (top + bottom)
  const BASE_CELL_PAD_H = 1.5 // horizontal padding per side
  const TITLE_H = 10

  const ptPerMm = pdf.internal.scaleFactor // jsPDF points-per-mm conversion

  // ── Step 1: measure minimum column width from header text ─────────────────
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(BASE_FONT_SIZE_HEADER)
  const minColWidths = data.columns.map((col) => {
    const textW = pdf.getStringUnitWidth(col) * BASE_FONT_SIZE_HEADER / ptPerMm
    return textW + BASE_CELL_PAD_H * 2
  })

  // ── Step 2: compute uniform scale so total ≤ imageMaxWidth ───────────────
  const totalMin = minColWidths.reduce((a, b) => a + b, 0)
  // scaleX < 1 → too wide (must shrink); scaleX > 1 → room to expand
  const scaleX = imageMaxWidth / Math.max(totalMin, 0.001)
  // Only shrink fonts/spacing; never inflate them above base size
  const fontScaleY = scaleX < 1 ? scaleX : 1.0

  const fontSizeHeader = BASE_FONT_SIZE_HEADER * fontScaleY
  const fontSizeData   = BASE_FONT_SIZE_DATA   * fontScaleY
  const lineH          = BASE_LINE_H           * fontScaleY
  const cellPadV       = BASE_CELL_PAD_V       * fontScaleY
  const cellPadH       = BASE_CELL_PAD_H       // keep horizontal padding fixed
  // Header row height: one line of header text + vertical padding
  const headerH = fontSizeHeader * 0.3528 + cellPadV * 2 // 0.3528 ≈ pt→mm

  // Final column widths: scale proportionally to fill exactly imageMaxWidth
  const colWidths = minColWidths.map((w) => w * scaleX)

  // Precompute column X starts
  const colXStarts: number[] = []
  let xAcc = margin
  for (const w of colWidths) {
    colXStarts.push(xAcc)
    xAcc += w
  }

  let y = imageTop

  // ── Title ─────────────────────────────────────────────────────────────────
  pdf.setFontSize(Math.min(11, 11 * fontScaleY))
  pdf.setTextColor(17, 24, 39)
  pdf.setFont('helvetica', 'bold')
  pdf.text(title, margin, y + 7 * fontScaleY)
  y += TITLE_H * fontScaleY

  // ── Header row ────────────────────────────────────────────────────────────
  const drawHeader = (yPos: number) => {
    pdf.setFillColor(249, 250, 251)
    pdf.rect(margin, yPos, imageMaxWidth, headerH, 'F')
    pdf.setDrawColor(229, 231, 235)
    pdf.line(margin, yPos + headerH, margin + imageMaxWidth, yPos + headerH)
    pdf.setFontSize(fontSizeHeader)
    pdf.setTextColor(107, 114, 128)
    pdf.setFont('helvetica', 'bold')
    data.columns.forEach((col, i) => {
      // Header text is guaranteed to fit in one line by construction of minColWidths
      pdf.text(col, (colXStarts[i] ?? margin) + cellPadH, yPos + headerH - cellPadV)
    })
  }

  drawHeader(y)
  y += headerH

  // ── Data rows ─────────────────────────────────────────────────────────────
  for (const row of data.rows) {
    // Pre-compute wrapped lines for every cell so we know the row height
    pdf.setFontSize(fontSizeData)
    pdf.setFont('helvetica', 'normal')
    const cellLines = data.columns.map((col, i) => {
      const val = row[col]
      const raw = val === null || val === undefined ? '—' : String(val)
      const availW = (colWidths[i] ?? 0) - cellPadH * 2
      return pdf.splitTextToSize(raw, Math.max(availW, 1)) as string[]
    })
    const maxLines = Math.max(...cellLines.map((l) => l.length), 1)
    const rowH = maxLines * lineH + cellPadV * 2

    // New page if this row would overflow
    if (y + rowH > pageHeight - margin) {
      pdf.addPage('a4', 'landscape')
      y = imageTop
      drawHeader(y)
      y += headerH
    }

    // Row separator
    pdf.setDrawColor(243, 244, 246)
    pdf.line(margin, y + rowH, margin + imageMaxWidth, y + rowH)

    // Cell text
    pdf.setFontSize(fontSizeData)
    pdf.setFont('helvetica', 'normal')
    data.columns.forEach((col, i) => {
      const lines = cellLines[i] ?? []
      const isNum = typeof row[col] === 'number'
      const xStart = colXStarts[i] ?? margin
      const colW   = colWidths[i] ?? 0
      lines.forEach((line, li) => {
        const textY = y + cellPadV + lineH * 0.8 + li * lineH
        pdf.setTextColor(55, 65, 81)
        if (isNum) {
          pdf.text(line, xStart + colW - cellPadH, textY, { align: 'right' })
        } else {
          pdf.text(line, xStart + cellPadH, textY)
        }
      })
    })

    y += rowH
  }

  // ── AI Insight box ────────────────────────────────────────────────────────
  if (insight) {
    const insightFontSize = Math.max(6, fontSizeData * 0.92)
    pdf.setFontSize(insightFontSize)
    pdf.setFont('helvetica', 'normal')
    const insightLines = pdf.splitTextToSize(insight, imageMaxWidth - cellPadH * 4) as string[]
    const labelH  = insightFontSize * 0.3528 + 3  // label row height in mm
    const bodyH   = insightLines.length * (insightFontSize * 0.3528 + 1)
    const boxH    = labelH + bodyH + cellPadV * 2 + 4

    y += 4  // gap above insight

    if (y + boxH > pageHeight - margin) {
      pdf.addPage('a4', 'landscape')
      y = imageTop
    }

    pdf.setFillColor(240, 253, 250)
    pdf.setDrawColor(167, 243, 208)
    pdf.roundedRect(margin, y, imageMaxWidth, boxH, 2, 2, 'FD')

    // "AI INSIGHT" label
    pdf.setFontSize(insightFontSize * 0.85)
    pdf.setTextColor(4, 120, 87)
    pdf.setFont('helvetica', 'bold')
    pdf.text('AI INSIGHT', margin + cellPadH * 2, y + labelH)

    // Insight body text
    pdf.setFontSize(insightFontSize)
    pdf.setTextColor(55, 65, 81)
    pdf.setFont('helvetica', 'normal')
    const textStartY = y + labelH + cellPadV + insightFontSize * 0.3528
    insightLines.forEach((line, li) => {
      pdf.text(line, margin + cellPadH * 2, textStartY + li * (insightFontSize * 0.3528 + 1))
    })
  }
}

// ── Main export function ───────────────────────────────────────────────────

export async function generateWorkspacePdfBlob(
  exportRootRef: React.RefObject<HTMLElement | null>,
  groupInsightExportRef: React.RefObject<HTMLElement | null>,
  clientId: string,
  reportBuilderGroupId: string | undefined,
): Promise<Blob | null> {
  const root = exportRootRef.current
  if (!root) return null

  const reportNodes = Array.from(
    root.querySelectorAll<HTMLElement>('[data-export-report="true"]'),
  ).filter((n) => n.offsetParent !== null) // exclude display:none nodes (e.g. mobile grid)

  const groupInsightNode = groupInsightExportRef.current
  const visibleGroupInsightNode =
    groupInsightNode && groupInsightNode.offsetParent !== null ? groupInsightNode : null

  if (reportNodes.length === 0 && !visibleGroupInsightNode) return null

  // html2canvas helper — reused for group-insight and fallback captures
  const captureNodeToCanvas = async (node: HTMLElement, forceWidth?: number) => {
    const captureWidth = forceWidth ?? node.scrollWidth
    let captureHeight = node.offsetHeight
    if (forceWidth != null && forceWidth !== node.scrollWidth) {
      const saved = node.style.width
      node.style.width = `${captureWidth}px`
      captureHeight = node.offsetHeight
      node.style.width = saved
    }
    return html2canvas(node, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      width: captureWidth,
      height: captureHeight,
      onclone: (_doc, cloned) => {
        cloned.style.overflow = 'visible'
        cloned.style.width = `${captureWidth}px`
        cloned.querySelectorAll<HTMLElement>('.overflow-hidden').forEach((el) => {
          el.style.overflow = 'visible'
        })
        cloned.querySelectorAll<HTMLElement>('.truncate').forEach((el) => {
          el.style.overflow = 'visible'
          el.style.textOverflow = 'clip'
          el.style.whiteSpace = 'normal'
        })
        cloned.querySelectorAll<HTMLElement>('.ant-select').forEach((selectEl) => {
          const valueEl = selectEl.querySelector<HTMLElement>('.ant-select-selection-item')
          const badge = document.createElement('span')
          badge.textContent = valueEl?.textContent ?? ''
          badge.style.cssText =
            'display:inline-flex;align-items:center;height:24px;padding:0 8px;' +
            'font-size:12px;color:#374151;background:#f9fafb;' +
            'white-space:nowrap;vertical-align:middle;text-align:center;text-decoration:underline;'
          selectEl.replaceWith(badge)
        })
      },
    })
  }

  let pdf: jsPDF | null = null
  let currentY = PDF_IMAGE_TOP
  const margin = PDF_MARGIN

  // ── Group insight: html2canvas (no report data, just prose text) ───────────
  if (visibleGroupInsightNode) {
    const referenceWidth = reportNodes[0]?.scrollWidth ?? visibleGroupInsightNode.scrollWidth
    pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageWidth = pdf.internal.pageSize.getWidth()
    const imageMaxWidth = pageWidth - margin * 2

    const canvas = await captureNodeToCanvas(visibleGroupInsightNode, referenceWidth)
    const mmPerPx = imageMaxWidth / canvas.width
    const pdfH = canvas.height * mmPerPx
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, currentY, imageMaxWidth, pdfH)
    currentY += pdfH + 6 // 6 mm gap before first report
    // Release canvas pixel buffer
    ;(canvas as unknown as { width: number }).width = 0
  }

  // ── Pre-fetch all report data in parallel ────────────────────────────────
  // Read all node metadata first so we can fire every fetch simultaneously.
  type ReportMeta = {
    vizType: PracticeAgentVisualizationType
    rbId: string
    exportTitle: string
    sortColumn: string | undefined
    sortDir: 'asc' | 'desc' | undefined
    groupByDim: string | undefined
    ddField: string
    ddValue: string
    ddDimension: string
    hasDrilldown: boolean
  }
  const supportedVizTypes = new Set<PracticeAgentVisualizationType>(['Bar', 'GroupedBar', 'Line', 'Table', 'KPI'])
  const reportMetas: ReportMeta[] = reportNodes.map((node, index) => {
    const rawVizType = node.dataset['vizType']
    const rbId = node.dataset['reportBuilderId'] ?? ''
    const exportTitle = node.dataset['exportTitle'] ?? `Report ${index + 1}`

    if (!rbId) {
      throw new Error(`Cannot export "${exportTitle}": missing report builder ID.`)
    }

    if (!rawVizType || !supportedVizTypes.has(rawVizType as PracticeAgentVisualizationType)) {
      throw new Error(`Cannot export "${exportTitle}" (${rbId}): unsupported visualization type "${rawVizType ?? ''}".`)
    }

    return {
      vizType: rawVizType as PracticeAgentVisualizationType,
      rbId,
      exportTitle,
      sortColumn:   node.dataset['exportSortColumn']   || undefined,
      sortDir:      (node.dataset['exportSortDirection'] || undefined) as 'asc' | 'desc' | undefined,
      groupByDim:   node.dataset['exportGroupBy']       || undefined,
      ddField:      node.dataset['exportDrilldownField'] ?? '',
      ddValue:      node.dataset['exportDrilldownValue'] ?? '',
      ddDimension:  node.dataset['exportDrilldownDimension'] ?? '',
      hasDrilldown: !!(node.dataset['exportDrilldownField'] && node.dataset['exportDrilldownDimension']),
    }
  })

  // Fire all data + insight fetches in parallel. A single failed report export
  // now aborts the PDF instead of silently embedding a partial DOM screenshot.
  type FetchResult = { allData: PracticeAgentReportData; insightText: string | undefined }
  const fetchResults: FetchResult[] = await Promise.all(
    reportMetas.map(async ({ rbId, vizType, exportTitle, sortColumn, sortDir, groupByDim, ddField, ddValue, ddDimension, hasDrilldown }): Promise<FetchResult> => {
      try {
        if (hasDrilldown) {
          const [drillResult, fetchedInsight] = await Promise.all([
            fetchDrilldownData(rbId, ddField, ddValue, ddDimension, 1, reportBuilderGroupId ?? undefined, true),
            fetchWorkspaceInsight({ clientId, reportBuilderId: rbId, filters: {} }),
          ])
          return {
            allData: {
              reportBuilderId: rbId,
              title: exportTitle,
              visualizationType: vizType,
              columns: drillResult.columns,
              rows: drillResult.rows,
              chartData: drillResult.chartData,
              kpiCards: drillResult.kpiCards,
              appliedFilters: {},
              retrievedAt: new Date().toISOString(),
              platformType: 0,
            },
            insightText: drillResult.insight || fetchedInsight?.insight || undefined,
          }
        } else {
          const [fetchedData, fetchedInsight] = await Promise.all([
            fetchReportData(
              rbId, clientId, {}, reportBuilderGroupId ?? undefined,
              undefined,    // page
              sortColumn,   // current committed sort column
              sortDir,      // current committed sort direction
              undefined,    // pageSize — not needed when allRows=true
              groupByDim,   // current group-by dimension
              true,         // allRows — fetch all data in one shot
            ),
            fetchWorkspaceInsight({ clientId, reportBuilderId: rbId, filters: {} }),
          ])
          return { allData: fetchedData, insightText: fetchedInsight?.insight || undefined }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        throw new Error(`Cannot export "${exportTitle}" (${rbId}): failed to fetch export data. ${message}`)
      }
    }),
  )

  // ── Report panels (render sequentially using pre-fetched data) ────────────
  for (let nodeIdx = 0; nodeIdx < reportMetas.length; nodeIdx++) {
    const meta = reportMetas[nodeIdx]
    const fetched = fetchResults[nodeIdx]
    if (!meta || !fetched) continue
    const { vizType, exportTitle, sortColumn, groupByDim } = meta
    const { allData, insightText } = fetched

    const isTable = vizType === 'Table'
    const isLine  = vizType === 'Line'
    const orientation: 'portrait' | 'landscape' = (isTable || isLine) ? 'landscape' : 'portrait'

    // Each report starts on its own page (group insight may have already created page 1)
    if (pdf === null) {
      pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' })
      currentY = PDF_IMAGE_TOP
    } else {
      pdf.addPage('a4', orientation)
      currentY = PDF_IMAGE_TOP
    }

    const pageWidth   = pdf.internal.pageSize.getWidth()
    const pageHeight  = pdf.internal.pageSize.getHeight()
    const imageMaxWidth = pageWidth - margin * 2

    // ── Derive chart data applying current groupBy (mirrors component logic) ─
    let chartData: PracticeAgentChartDataPoint[] = allData.chartData
    if (allData.columns.length > 0 && allData.rows.length > 0) {
      const { dimensionCols, measureCols } = classifyColumns(allData.columns, allData.rows)
      const labelCol = groupByDim ?? allData.groupByDimension ?? pickLabelColumn(dimensionCols, allData.rows)
      if (labelCol) {
        const activeMeasure = sortColumn && measureCols.includes(sortColumn) ? sortColumn : (measureCols[0] ?? null)
        const derived = deriveChartDataForDimension(allData.columns, allData.rows, labelCol, activeMeasure ?? undefined)
        if (derived.length > 0) chartData = derived
      }
    }

    const hasInvalidChartValue = chartData.some(d => !Number.isFinite(d.value))
    if ((vizType === 'Bar' || vizType === 'Line') && hasInvalidChartValue) {
      throw new Error(`Cannot export "${exportTitle}" (${meta.rbId}): ${vizType} chart data contains invalid values.`)
    }

    if (vizType === 'GroupedBar' && !chartData.some(d => d.series && d.series.length > 0)) {
      throw new Error(`Cannot export "${exportTitle}" (${meta.rbId}): GroupedBar data does not include grouped series.`)
    }

    if (vizType === 'KPI' && allData.kpiCards.length === 0) {
      throw new Error(`Cannot export "${exportTitle}" (${meta.rbId}): KPI data does not include KPI cards.`)
    }

    // ── Dispatch to the appropriate native renderer ────────────────────────
    switch (vizType) {
      case 'Table':
        renderTableDirectToPdf(
          pdf, allData,
          allData.title || exportTitle,
          insightText, margin, currentY, imageMaxWidth, pageHeight,
        )
        break

      case 'Bar':
        currentY = renderBarChartDirectToPdf(
          pdf, chartData, exportTitle, insightText, margin, currentY, imageMaxWidth, pageHeight,
        )
        break

      case 'Line':
        currentY = renderLineChartDirectToPdf(
          pdf, chartData, exportTitle, insightText, margin, currentY, imageMaxWidth, pageHeight,
          allData.axisMinValue,
          allData.axisMaxValue,
        )
        break

      case 'GroupedBar': {
        currentY = renderGroupedBarChartDirectToPdf(pdf, chartData, exportTitle, insightText, margin, currentY, imageMaxWidth, pageHeight)
        break
      }

      case 'KPI':
        currentY = renderKpiDirectToPdf(
          pdf, allData.kpiCards, chartData, exportTitle, insightText, margin, currentY, imageMaxWidth, pageHeight,
        )
        break

      default:
        throw new Error(`Cannot export "${exportTitle}" (${meta.rbId}): unsupported visualization type "${vizType}".`)
    }

    // allData eligible for GC before next iteration — no explicit ref to keep
  }

  if (!pdf) return null
  return pdf.output('blob')
}
